import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { analyzeVolumeBalance, calculateVolumeTrends, VolumeAnalysis, VolumeTrendPoint } from "@/lib/volume-balance";

interface UseVolumeBalanceOptions {
  clientId?: string;
  weeksBack?: number;
}

interface VolumeBalanceResult {
  analysis: VolumeAnalysis | null;
  trends: {
    weekly: VolumeTrendPoint[];
    monthly: VolumeTrendPoint[];
  };
  loading: boolean;
  error: string | null;
}

export function useVolumeBalance({ clientId, weeksBack = 4 }: UseVolumeBalanceOptions = {}): VolumeBalanceResult {
  const [analysis, setAnalysis] = useState<VolumeAnalysis | null>(null);
  const [trends, setTrends] = useState<{ weekly: VolumeTrendPoint[]; monthly: VolumeTrendPoint[] }>({
    weekly: [],
    monthly: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function loadVolumeData() {
      try {
        setLoading(true);
        setError(null);

        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          setError("Not authenticated");
          return;
        }

        // For trends, we want more historical data (12 weeks)
        const trendWeeks = Math.max(weeksBack, 12);
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - trendWeeks * 7);

        // Build query for workout logs
        let workoutLogsQuery = supabase
          .from("workout_logs")
          .select("id, started_at")
          .gte("started_at", startDate.toISOString())
          .not("completed_at", "is", null);

        if (clientId) {
          workoutLogsQuery = workoutLogsQuery.eq("client_id", clientId);
        } else {
          workoutLogsQuery = workoutLogsQuery.eq("logged_by", user.id);
        }

        const { data: workoutLogs, error: logsError } = await workoutLogsQuery;

        if (logsError) {
          console.error("Error fetching workout logs:", logsError);
          setError("Failed to load workout data");
          return;
        }

        if (!workoutLogs || workoutLogs.length === 0) {
          setAnalysis({
            muscleGroups: [],
            totalSets: 0,
            totalVolume: 0,
            imbalances: [],
            weeklyAverage: { sets: 0, volume: 0 },
          });
          setTrends({ weekly: [], monthly: [] });
          return;
        }

        const workoutLogIds = workoutLogs.map((wl) => wl.id);

        // Fetch set logs with exercise data
        const { data: setLogs, error: setLogsError } = await supabase
          .from("set_logs")
          .select(`
            id,
            exercise_id,
            weight,
            reps_completed,
            workout_log_id,
            exercises (
              name,
              muscle_groups
            )
          `)
          .in("workout_log_id", workoutLogIds);

        if (setLogsError) {
          console.error("Error fetching set logs:", setLogsError);
          setError("Failed to load set data");
          return;
        }

        // Map the data for analysis
        const mappedData = (setLogs || []).map((log) => {
          const workoutLog = workoutLogs.find((wl) => wl.id === log.workout_log_id);
          const exercise = log.exercises as { name: string; muscle_groups: { primary: string[]; secondary: string[] } | null } | null;
          
          return {
            exercise_id: log.exercise_id,
            exercise_name: exercise?.name || "Unknown",
            muscle_groups: exercise?.muscle_groups || null,
            weight: log.weight,
            reps_completed: log.reps_completed,
            workout_date: workoutLog?.started_at || new Date().toISOString(),
          };
        });

        // Filter data for the requested weeksBack for main analysis
        const analysisStartDate = new Date();
        analysisStartDate.setDate(analysisStartDate.getDate() - weeksBack * 7);
        const filteredForAnalysis = mappedData.filter(
          (d) => new Date(d.workout_date) >= analysisStartDate
        );

        const result = analyzeVolumeBalance(filteredForAnalysis, weeksBack);
        setAnalysis(result);

        // Calculate trends using all data
        const trendData = (setLogs || []).map((log) => {
          const workoutLog = workoutLogs.find((wl) => wl.id === log.workout_log_id);
          return {
            weight: log.weight,
            reps_completed: log.reps_completed,
            workout_date: workoutLog?.started_at || new Date().toISOString(),
            workout_id: log.workout_log_id,
          };
        });

        const weeklyTrends = calculateVolumeTrends(trendData, "weekly", trendWeeks);
        const monthlyTrends = calculateVolumeTrends(trendData, "monthly", trendWeeks);

        setTrends({ weekly: weeklyTrends, monthly: monthlyTrends });
      } catch (err) {
        console.error("Error in volume balance analysis:", err);
        setError("An error occurred");
      } finally {
        setLoading(false);
      }
    }

    loadVolumeData();
  }, [clientId, weeksBack]);

  return { analysis, trends, loading, error };
}
